import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { setStoredToken, clearStoredToken } from "../lib/axios-setup";
import { clearPianoLock } from "../lib/piano-lock";

const API = window.location.origin;
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/auth/me`, { withCredentials: true });
      // Aggiorna `user` SOLO se i dati sono effettivamente cambiati.
      // Il refetch periodico ogni 60s altrimenti causerebbe un re-render
      // di tutta la subtree di AuthProvider (Navbar, ChiamaPaziente con
      // 100+ opp, Archivio, ecc.) inutilmente, rendendo la UI laggosa.
      // Confronto JSON è sufficiente (oggetti piccoli, no funzioni).
      setUser((prev) => {
        try {
          if (prev && JSON.stringify(prev) === JSON.stringify(res.data)) return prev;
        } catch { /* fallback al replace */ }
        return res.data;
      });
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    fetchUser();
  }, [fetchUser]);

  // Periodic refetch of /auth/me every 60s. Keeps the frontend `user`
  // object in sync with the DB so that role / allowed_pages / feature
  // flag changes (made by an admin via Gestione Utenti) propagate to
  // open sessions without requiring a manual logout/login.
  // Skipped when no user is logged in (avoids 401 noise) and when the
  // tab is hidden (saves bandwidth/requests).
  // Usiamo `!!user` (boolean) come dep per non ri-creare l'interval
  // ogni volta che `user` viene rimpiazzato — il fetcher è stabile via
  // useCallback.
  const isLoggedIn = !!user;
  useEffect(() => {
    if (!isLoggedIn) return undefined;
    const REFRESH_MS = 60_000;
    const interval = setInterval(() => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      fetchUser();
    }, REFRESH_MS);
    return () => clearInterval(interval);
  }, [isLoggedIn, fetchUser]);

  const login = useCallback(async (email, password) => {
    const res = await axios.post(`${API}/api/auth/login`, { email, password }, { withCredentials: true });
    // Store token for Bearer-header auth (needed in GHL iframe where
    // third-party cookies are blocked). Cookies still work as fallback.
    if (res.data?.token) setStoredToken(res.data.token);
    // Piano di Cura page lock is reset at every login.
    clearPianoLock();
    setUser(res.data);
    return res.data;
  }, []);

  const loginWithGoogle = useCallback(async (sessionId) => {
    const res = await axios.post(`${API}/api/auth/google`, { session_id: sessionId }, { withCredentials: true });
    if (res.data?.token) setStoredToken(res.data.token);
    clearPianoLock();
    setUser(res.data);
    return res.data;
  }, []);

  // Custom Google OAuth (Google Identity Services). Riceve direttamente
  // l'ID token JWT dal frontend e lo verifica nel backend. Bypassa
  // l'auth Emergent-managed: nessuno step intermedio, zero redirect a
  // domini esterni durante il flow.
  const loginWithGoogleCustom = useCallback(async (credential) => {
    const res = await axios.post(`${API}/api/auth/google/custom`, { credential }, { withCredentials: true });
    if (res.data?.token) setStoredToken(res.data.token);
    clearPianoLock();
    setUser(res.data);
    return res.data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/api/auth/logout`, {}, { withCredentials: true });
    } finally {
      clearStoredToken();
      clearPianoLock();
      setUser(null);
    }
  }, []);

  // ctxValue: login/loginWithGoogle/loginWithGoogleCustom/logout/fetchUser
  // sono già stabili (useCallback con deps=[]), quindi le uniche dep reali
  // del memo sono user e loading. Manteniamo il memo per evitare re-render
  // a cascata dei consumer di AuthContext.
  const ctxValue = useMemo(
    () => ({ user, loading, login, loginWithGoogle, loginWithGoogleCustom, logout, fetchUser }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, loading]
  );

  return (
    <AuthContext.Provider value={ctxValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
